import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ClubAutocomplete } from '@/components/club/ClubAutocomplete';
import { CityCombobox } from '@/components/geo/CityCombobox';
import { ROUTES } from '@/config/routes';
import { createClubApi, createClubClaimApi, getClubApi, updateClubContactApi } from '@/lib/api/clubsApi';
import { getMeApi } from '@/lib/auth/authApi.ts';
import { clubOnboardingSchema } from '@/lib/validation/clubOnboardingSchema';
import { useAuthStore } from '@/stores/auth-store';
import type { ClubAutocompleteItem } from '@api-client/clubs';
import type { Commune } from '@shared/types/entities';
import { AlertTriangle, Building2, Check, ChevronRight, CircleDot, Clock, Phone, Shield, Tag, X } from 'lucide-react';

function slugify(str: string) {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export default function CreateClubOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, accessToken } = useAuthStore();

  // ─── State ────────────────────────────────────────────
  const [name, setName] = useState('');
  const [alias, setAlias] = useState('');
  const [city, setCity] = useState('');
  const [department, setDepartment] = useState('');
  const [region, setRegion] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Existing club claim flow
  const [selectedClub, setSelectedClub] = useState<ClubAutocompleteItem | null>(null);
  const [isAdminQuestion, setIsAdminQuestion] = useState<boolean | null>(null);
  const [claimSuccess, setClaimSuccess] = useState(false);

  const isExistingClubMode = !!selectedClub;

  // ─── Handlers ─────────────────────────────────────────

  const handleNameBlur = () => {
    if (!selectedClub && !alias && name) setAlias(slugify(name));
  };

  const handleClubSelect = (club: ClubAutocompleteItem) => {
    setSelectedClub(club);
    setName(club.name);
    setCity(club.city);
    setDepartment(club.department);
    setRegion('');
    setAlias('');
    setIsAdminQuestion(null);
    setError(null);
  };

  // Pre-fill from URL param (coming from club-profile page)
  const claimClubIdParam = searchParams.get('claimClubId');
  useEffect(() => {
    if (!claimClubIdParam) return;
    const clubId = Number(claimClubIdParam);
    if (isNaN(clubId)) return;
    getClubApi(clubId)
      .then(club => {
        handleClubSelect({
          id: club.id,
          name: club.name,
          city: club.city || '',
          department: club.department || '',
          hasAdmin: !!club.hasAdmin,
        });
      })
      .catch(() => {
        /* club not found — ignore */
      });
  }, [claimClubIdParam]);

  const handleClubClear = () => {
    setSelectedClub(null);
    setName('');
    setCity('');
    setDepartment('');
    setRegion('');
    setAlias('');
    setEmail('');
    setPhone('');
    setIsAdminQuestion(null);
    setError(null);
  };

  const handleCitySelect = (commune: Commune) => {
    setCity(commune.name);
    setDepartment(commune.departmentName ?? commune.department);
    setRegion(commune.regionName ?? commune.region ?? '');
  };

  const handleCityChange = (value: string) => {
    setCity(value);
    if (!value) {
      setDepartment('');
      setRegion('');
    }
  };

  // ─── Submit: claim existing club ──────────────────────

  const handleClaimSubmit = async () => {
    if (!selectedClub) return;
    setError(null);
    setIsSubmitting(true);

    try {
      // Create the claim (API promotes user to CLUB_ADMIN + attaches to club)
      await createClubClaimApi(selectedClub.id);

      // Update email/phone if provided
      if (email || phone) {
        await updateClubContactApi(selectedClub.id, {
          ...(email && { email }),
          ...(phone && { phone }),
        });
      }

      // Refresh auth state (user is now CLUB_ADMIN)
      const updatedUser = await getMeApi();
      login(updatedUser, accessToken!);

      setClaimSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la demande');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Submit: create new club ──────────────────────────

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    const result = clubOnboardingSchema.safeParse({
      name,
      alias,
      city,
      department,
      region,
      email,
      phone: phone || undefined,
    });

    if (!result.success) {
      const errs: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0]?.toString();
        if (key && !errs[key]) errs[key] = issue.message;
      }
      setFieldErrors(errs);
      setError(Object.values(errs)[0] ?? 'Veuillez corriger les erreurs');
      return;
    }

    setIsSubmitting(true);

    try {
      await createClubApi({
        name: result.data.name,
        alias: result.data.alias,
        city: result.data.city,
        department: result.data.department,
        region: result.data.region,
        email: result.data.email,
        phone: result.data.phone || undefined,
      });

      const updatedUser = await getMeApi();
      login(updatedUser, accessToken!);

      navigate(ROUTES.CLUB_DASHBOARD, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la création du club');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Claim success screen ─────────────────────────────

  if (claimSuccess && selectedClub) {
    return (
      <div className='min-h-screen bg-linear-to-br from-emerald-600 to-emerald-400 flex flex-col items-center justify-center px-6 py-12'>
        <div className='flex items-center gap-3 mb-8'>
          <CircleDot className='w-10 h-10 text-white' />
          <span className='text-2xl font-extrabold text-white'>BoulesConnect</span>
        </div>

        <div className='w-full max-w-lg bg-white rounded-3xl p-8 shadow-xl'>
          <div className='flex flex-col items-center text-center mb-6'>
            <div className='w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mb-4'>
              <Clock className='w-8 h-8 text-indigo-600' />
            </div>
            <h2 className='text-2xl font-extrabold text-slate-900 mb-2'>Demande envoyée !</h2>
            <p className='text-slate-500 text-sm max-w-sm'>
              Votre demande pour devenir administrateur de{' '}
              <span className='font-bold text-slate-700'>{selectedClub.name}</span> a bien été enregistrée.
            </p>
          </div>

          <div className='bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-6'>
            <h3 className='font-bold text-indigo-900 text-sm mb-2 flex items-center gap-2'>
              <Shield className='w-4 h-4' />
              Prochaines étapes
            </h3>
            <ul className='space-y-2 text-sm text-indigo-700'>
              <li className='flex items-start gap-2'>
                <span className='font-bold text-indigo-500 shrink-0'>1.</span>
                <span>3 utilisateurs (Compte joueur) doivent valider votre demande en visitant la page du club</span>
              </li>
              <li className='flex items-start gap-2'>
                <span className='font-bold text-indigo-500 shrink-0'>2.</span>
                <span>Une fois validée, votre page club deviendra active</span>
              </li>
              <li className='flex items-start gap-2'>
                <span className='font-bold text-indigo-500 shrink-0'>3.</span>
                <span>Vous pourrez alors gérer les informations, membres et concours du club</span>
              </li>
            </ul>
          </div>

          <div className='flex gap-3'>
            <button
              onClick={() => navigate(ROUTES.CLUB_DASHBOARD, { replace: true })}
              className='flex-1 flex items-center justify-center gap-2 bg-indigo-600 text-white py-3 rounded-xl font-bold text-sm hover:bg-indigo-700 transition-colors cursor-pointer'
            >
              Accéder au tableau de bord <ChevronRight className='w-4 h-4' />
            </button>
            <a
              href={`${import.meta.env.VITE_URL_FRONT || 'http://localhost:5232'}/clubs/${selectedClub.id}`}
              className='flex items-center gap-2 border border-slate-200 text-slate-600 px-4 py-3 rounded-xl font-semibold text-sm hover:bg-slate-50 transition-colors'
            >
              Page publique
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ─── Main form ────────────────────────────────────────

  return (
    <div className='min-h-screen bg-linear-to-br from-emerald-600 to-emerald-400 flex flex-col items-center justify-center px-6 py-12'>
      {/* Brand */}
      <div className='flex items-center gap-3 mb-8'>
        <CircleDot className='w-10 h-10 text-white' />
        <span className='text-2xl font-extrabold text-white'>BoulesConnect</span>
      </div>

      <div className='w-full max-w-lg bg-white rounded-3xl p-8 shadow-xl'>
        <div className='flex items-center gap-3 mb-6'>
          <div className='w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center'>
            <Building2 className='w-6 h-6 text-emerald-600' />
          </div>
          <div>
            <h1 className='text-xl font-extrabold text-slate-900'>
              {isExistingClubMode ? 'Revendiquer un club' : 'Créer mon club'}
            </h1>
            <p className='text-sm text-slate-500'>
              {isExistingClubMode
                ? 'Confirmez vos informations et demandez le rôle administrateur.'
                : 'Saisissez le nom de votre club ou créez-en un nouveau.'}
            </p>
          </div>
        </div>

        {error && (
          <div className='mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700'>{error}</div>
        )}

        <form onSubmit={isExistingClubMode ? e => e.preventDefault() : handleCreateSubmit} className='space-y-4'>
          {/* Nom du club — autocomplete */}
          <div>
            <label className='block text-sm font-semibold text-slate-700 mb-1.5'>
              Nom du club <span className='text-red-500'>*</span>
            </label>
            <ClubAutocomplete
              value={name}
              onChange={v => {
                setName(v);
                if (selectedClub) handleClubClear();
              }}
              onSelect={handleClubSelect}
              onClear={handleClubClear}
              placeholder='Rechercher ou saisir le nom de votre club…'
            />
            {!isExistingClubMode && name.length >= 2 && (
              <p className='text-xs text-slate-400 mt-1'>Aucun club trouvé ? Continuez pour en créer un nouveau.</p>
            )}
            {fieldErrors.name && <p className='text-xs text-red-500 mt-1'>{fieldErrors.name}</p>}
          </div>

          {/* ── Existing club selected ── */}
          {isExistingClubMode && (
            <>
              {/* Club info summary */}
              <div className='bg-slate-50 border border-slate-200 rounded-xl p-4'>
                <div className='flex items-center gap-3'>
                  <Building2 className='w-5 h-5 text-slate-400 shrink-0' />
                  <div>
                    <p className='font-bold text-slate-900 text-sm'>{selectedClub!.name}</p>
                    <p className='text-xs text-slate-500'>
                      {selectedClub!.city} ({selectedClub!.department})
                    </p>
                  </div>
                  {selectedClub!.hasAdmin && (
                    <span className='ml-auto px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-bold'>
                      Admin actif
                    </span>
                  )}
                </div>
              </div>

              {/* Club déjà administré → revendication impossible (sécurité) */}
              {selectedClub!.hasAdmin && (
                <div className='bg-amber-50 border border-amber-200 rounded-xl p-4'>
                  <div className='flex items-start gap-3'>
                    <AlertTriangle className='w-5 h-5 text-amber-500 shrink-0 mt-0.5' />
                    <div>
                      <p className='font-bold text-amber-900 text-sm mb-1'>Ce club a déjà un administrateur</p>
                      <p className='text-xs text-amber-700 leading-relaxed'>
                        Ce club est déjà géré par un administrateur. Il n'est donc pas possible de le revendiquer. Si
                        vous êtes un joueur membre de ce club, créez un compte joueur puis demandez l'adhésion depuis sa
                        page publique. Si vous pensez qu'il s'agit d'une erreur, contactez le support.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Parcours de revendication — uniquement si le club n'a pas encore d'admin */}
              {!selectedClub!.hasAdmin && (
                <>
                  {/* Admin question */}
                  <div className='bg-indigo-50 border border-indigo-200 rounded-xl p-4'>
                    <p className='font-bold text-indigo-900 text-sm mb-3'>
                      Êtes-vous l'un des administrateurs de ce club ?
                    </p>
                    <div className='flex gap-3'>
                      <button
                        type='button'
                        onClick={() => setIsAdminQuestion(true)}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm transition-colors cursor-pointer ${
                          isAdminQuestion === true
                            ? 'bg-indigo-600 text-white'
                            : 'bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-100'
                        }`}
                      >
                        <Check className='w-4 h-4' />
                        Oui
                      </button>
                      <button
                        type='button'
                        onClick={() => setIsAdminQuestion(false)}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm transition-colors cursor-pointer ${
                          isAdminQuestion === false
                            ? 'bg-red-600 text-white'
                            : 'bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-100'
                        }`}
                      >
                        <X className='w-4 h-4' />
                        Non
                      </button>
                    </div>
                  </div>

                  {/* Answer: YES → claim flow */}
                  {isAdminQuestion === true && (
                    <>
                      {/* Email / Phone editable */}
                      <div className='grid grid-cols-2 gap-3'>
                        <div>
                          <label className='block text-sm font-semibold text-slate-700 mb-1.5'>
                            Email du club <span className='text-red-500'>*</span>
                          </label>
                          <input
                            type='email'
                            maxLength={255}
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            placeholder='contact@monclub.fr'
                            className='w-full px-3 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors text-sm'
                          />
                        </div>
                        <div>
                          <label className='block text-sm font-semibold text-slate-700 mb-1.5'>Téléphone</label>
                          <div className='relative'>
                            <Phone className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
                            <input
                              type='tel'
                              maxLength={15}
                              value={phone}
                              onChange={e => setPhone(e.target.value)}
                              placeholder='06 00 00 00 00'
                              className='w-full pl-9 pr-3 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors text-sm'
                            />
                          </div>
                        </div>
                      </div>

                      <div className='bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700'>
                        <p className='font-semibold mb-1'>Important</p>
                        <p>
                          Vous serez inscrit comme administrateur de ce club mais votre page club ne sera active qu'une
                          fois que <strong>3 joueurs</strong> auront validé votre demande depuis la page du club.
                        </p>
                      </div>

                      <button
                        type='button'
                        onClick={handleClaimSubmit}
                        disabled={isSubmitting}
                        className='w-full flex items-center justify-center gap-2 bg-indigo-600 text-white font-bold py-3 rounded-xl hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors'
                      >
                        {isSubmitting ? (
                          'Envoi en cours…'
                        ) : (
                          <span className={'flex flex-row items-center cursor-pointer'}>
                            <span>
                              {' '}
                              <Shield className='w-4 h-4 mr-2' />
                            </span>
                            <span>Revendiquer ce club</span>
                          </span>
                        )}
                      </button>
                    </>
                  )}

                  {/* Answer: NO → warning message */}
                  {isAdminQuestion === false && (
                    <div className='bg-amber-50 border border-amber-200 rounded-xl p-4'>
                      <div className='flex items-start gap-3'>
                        <AlertTriangle className='w-5 h-5 text-amber-500 shrink-0 mt-0.5' />
                        <div>
                          <p className='font-bold text-amber-900 text-sm mb-1'>Accès réservé aux administrateurs</p>
                          <p className='text-xs text-amber-700 leading-relaxed'>
                            Cet accès club est uniquement pour les administrateurs du club. Si vous êtes un joueur
                            membre de ce club, créez un compte joueur puis demandez l'adhésion à votre club depuis sa
                            page publique. L'administrateur du club validera votre demande.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* ── New club creation form ── */}
          {!isExistingClubMode && name.length > 0 && (
            <>
              {/* Alias */}
              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-1.5'>
                  Identifiant unique (alias) <span className='text-red-500'>*</span>
                </label>
                <div className='relative'>
                  <Tag className='absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
                  <input
                    type='text'
                    required
                    maxLength={100}
                    pattern='[a-z0-9]+(?:-[a-z0-9]+)*'
                    title='Lettres minuscules, chiffres et tirets uniquement'
                    value={alias}
                    onChange={e => setAlias(e.target.value)}
                    onBlur={handleNameBlur}
                    placeholder='boule-club-marseille'
                    className={`w-full pl-11 pr-4 py-3 bg-slate-50 border rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors font-mono text-sm ${fieldErrors.alias ? 'border-red-300' : 'border-slate-200'}`}
                  />
                </div>
                {fieldErrors.alias ? (
                  <p className='text-xs text-red-500 mt-1'>{fieldErrors.alias}</p>
                ) : (
                  <p className='text-xs text-slate-400 mt-1'>Minuscules, chiffres et tirets uniquement.</p>
                )}
              </div>

              {/* Ville */}
              <div>
                <label className='block text-sm font-semibold text-slate-700 mb-1.5'>
                  Ville <span className='text-red-500'>*</span>
                </label>
                <CityCombobox
                  value={city}
                  onChange={handleCityChange}
                  onSelect={handleCitySelect}
                  placeholder='Rechercher une commune…'
                  className='[&_input]:py-3'
                />
                {fieldErrors.city && <p className='text-xs text-red-500 mt-1'>{fieldErrors.city}</p>}
              </div>

              <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                <div>
                  <label className='block text-sm font-semibold text-slate-700 mb-1.5'>
                    Département <span className='text-red-500'>*</span>
                  </label>
                  <input
                    type='text'
                    readOnly
                    maxLength={100}
                    value={department}
                    placeholder='Rempli automatiquement'
                    className='w-full px-3 py-3 bg-slate-100 border border-slate-200 rounded-xl text-slate-700 placeholder:text-slate-400 cursor-not-allowed text-sm'
                  />
                  {fieldErrors.department && <p className='text-xs text-red-500 mt-1'>{fieldErrors.department}</p>}
                </div>
                <div>
                  <label className='block text-sm font-semibold text-slate-700 mb-1.5'>
                    Région <span className='text-red-500'>*</span>
                  </label>
                  <input
                    type='text'
                    readOnly
                    maxLength={100}
                    value={region}
                    placeholder='Rempli automatiquement'
                    className='w-full px-3 py-3 bg-slate-100 border border-slate-200 rounded-xl text-slate-700 placeholder:text-slate-400 cursor-not-allowed text-sm'
                  />
                  {fieldErrors.region && <p className='text-xs text-red-500 mt-1'>{fieldErrors.region}</p>}
                </div>
              </div>

              {/* Email / Phone */}
              <div className='grid grid-cols-2 gap-3'>
                <div>
                  <label className='block text-sm font-semibold text-slate-700 mb-1.5'>
                    Email du club <span className='text-red-500'>*</span>
                  </label>
                  <input
                    type='email'
                    required
                    maxLength={255}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder='contact@monclub.fr'
                    className={`w-full px-3 py-3 bg-slate-50 border rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors text-sm ${fieldErrors.email ? 'border-red-300' : 'border-slate-200'}`}
                  />
                  <p className='text-xs text-slate-400 mt-1'>
                    Servira d&apos;email de facturation pour votre abonnement.
                  </p>
                  {fieldErrors.email && <p className='text-xs text-red-500 mt-1'>{fieldErrors.email}</p>}
                </div>
                <div>
                  <label className='block text-sm font-semibold text-slate-700 mb-1.5'>Téléphone</label>
                  <div className='relative'>
                    <Phone className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' />
                    <input
                      type='tel'
                      maxLength={15}
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      placeholder='06 00 00 00 00'
                      className={`w-full pl-9 pr-3 py-3 bg-slate-50 border rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors text-sm ${fieldErrors.phone ? 'border-red-300' : 'border-slate-200'}`}
                    />
                  </div>
                  {fieldErrors.phone && <p className='text-xs text-red-500 mt-1'>{fieldErrors.phone}</p>}
                </div>
              </div>

              <button
                type='submit'
                disabled={isSubmitting}
                className='w-full flex items-center justify-center gap-2 bg-emerald-600 text-white font-bold py-3 rounded-xl hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors mt-2'
              >
                {isSubmitting ? (
                  'Création en cours…'
                ) : (
                  <>
                    Créer mon club <ChevronRight className='w-4 h-4' />
                  </>
                )}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
